import React, { memo, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Button } from 'transfix-ui/components/Button'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage } from 'transfix-ui/components/InlineMessage'
import { useRefresh } from 'utils/hooks/useRefresh'
import { CustomerAutosuggest } from 'apps/fleet-planner/components/atoms'
import {
  DisplayTile,
  DisplayTileHeader,
  DisplayTileHeaderAction,
  DisplayTileSection,
} from 'apps/fleet-planner/components/atoms/DisplayTile'
import { CustomerContact } from 'apps/fleet-planner/components/form-components/CustomerContact'
import { CustomerForm } from 'apps/fleet-planner/components/forms/CustomerForm'
import { AddEntityModal } from 'apps/fleet-planner/components/modals/AddEntityModal'
import { FP_SUPPORT_EMAIL } from 'apps/fleet-planner/config'
import { useCustomerApi, useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useCustomerInputState } from 'apps/fleet-planner/hooks/userCustomerInputState'
import { CustomerSchemaType } from 'apps/fleet-planner/schema/CustomerSchema'
import * as Types from 'apps/fleet-planner/types'
import { createCustomerSuccess } from 'apps/fleet-planner/utils/actions/customers'
import { Bullet } from 'components/atoms'
import { AddIcon, EditIcon } from 'transfix-icons'
import formatPhone from 'utils/formatPhone'
import { DISPLAY } from 'constants/display'

export interface ICustomerProps
  extends Pick<Types.ILoad, 'customerNotes' | 'customer' | 'customerContact'> {
  /**
   * @default true
   */
  large?: boolean
  onChange: (change: Partial<Types.ILoad>) => void
  onValidationChange?: (message?: string) => void
}

export type EditingFields =
  | 'customerNotes'
  | 'customerId'
  | 'customer'
  | 'customerContact'
  | 'customerContactId'

export const Customer = memo<ICustomerProps>(
  ({
    customer: customerProp,
    customerContact: customerContactProp,
    customerNotes: customerNotesProp,
    large: largeProp = true,
    onChange: onChangeProp,
    onValidationChange: onValidationChangeProp,
  }) => {
    const { trackEvent } = useFMSAnalytics()
    const dispatch = useDispatch()

    const [inputValue, setInputValue] = useState<string | undefined>('')
    const { getErrorMessage, onBlurTrigger, onChangeTrigger } = useCustomerInputState()
    const [isFormVisible, setFormVisible] = useState<boolean>(false)
    const [defaultNewCustomerName, setDefaultNewCustomerName] = useState<string>('')

    const customerContactChangeHandler = (changes: Partial<Pick<Types.ILoad, EditingFields>>) => {
      setFormVisible(false)
      onChangeProp(changes)
    }

    const { createNewCustomer, error: apiError, isLoading: isApiLoading } = useCustomerApi(
      undefined,
      false
    )

    // Reset the autocomplete input when customer updates
    const { key: autocompleteKey, resetKey } = useRefresh()
    useEffect(() => {
      if (customerProp && !!customerProp?.name) resetKey()
    }, [customerProp, resetKey])

    const cancelAddEntityHandler = () => {
      setFormVisible(false)
      resetKey()
    }

    const addEntityHandler = (val?: string) => {
      // Clear any current values
      onChangeProp({
        customer: undefined,
        customerId: undefined,
        customerNotes: undefined,
        customerContact: undefined,
        customerContactId: undefined,
      })
      setDefaultNewCustomerName(val || '')
      setFormVisible(true)
    }

    /**
     * Pass updates to parent component
     */
    const changeHandler = (selectedCustomer: Partial<Types.ICustomer> | undefined) => {
      const { id: cId, notes: cNotes, contacts = [] } = selectedCustomer || {}

      setInputValue(selectedCustomer?.name || '')

      const customerContact = contacts.find(
        c => c.purpose === Types.CUSTOMER_CONTACT_PURPOSE.PRIMARY
      )

      const { id: customerContactId } = customerContact || {}

      onChangeProp({
        customer: selectedCustomer as Types.ICustomer,
        customerId: cId,
        customerNotes: cNotes,
        customerContact,
        customerContactId,
      })

      onChangeTrigger()
    }

    const addEntityClickHandler = () => addEntityHandler('')

    /**
     * Triggered by <CustomerForm />
     */
    const addNewCustomerHandler = async (c: CustomerSchemaType) => {
      const newCustomer = await createNewCustomer(c)

      if (newCustomer) {
        // Update Redux
        dispatch(createCustomerSuccess(newCustomer))

        // Send update to parent component
        changeHandler(newCustomer)

        // Hide form
        setFormVisible(false)

        // Re-mount Autocomplete to init state
        resetKey()
      }
    }

    const editCustomerClickHandler = () => setFormVisible(true)

    const defaultValues = {
      ...customerProp,
      name: customerProp?.name || defaultNewCustomerName,
    }

    const inputChangeHandler = (val: string = '') => {
      setInputValue(val)
      onChangeTrigger()
    }

    const blurHandler = () => {
      onBlurTrigger()
    }

    const error = getErrorMessage({
      value: customerProp,
      inputValue,
    })

    // When error message updates, send to parent component
    useEffect(() => {
      if (onValidationChangeProp) onValidationChangeProp(error)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error])

    const customersBulkUploadClickHandler = () =>
      trackEvent('Bulk Upload', 'CLICK_CUSTOMERS_BULK_UPLOAD_LINK')

    return (
      <>
        <div>
          <CustomerAutosuggest
            large={largeProp}
            key={autocompleteKey}
            value={customerProp}
            onChange={changeHandler}
            onAddEntity={addEntityHandler}
            disabled={isFormVisible}
            onInputChange={inputChangeHandler}
            error={error}
            onBlur={blurHandler}
          />

          {!customerProp?.id && (
            <Button
              startIcon={<AddIcon />}
              variant='text'
              data-test='add-new-customer-cta'
              onClick={addEntityClickHandler}>
              Add new customer
            </Button>
          )}

          {isFormVisible && !customerProp?.id && (
            <AddEntityModal
              open
              title='Add New Customer'
              error={apiError}
              dataTest='add-customer-modal'
              onClose={cancelAddEntityHandler}>
              <InlineMessage
                className='mb5 mt3 mt0-m'
                message={
                  <span>
                    <b>Lots of customers to add?</b>{' '}
                    <a
                      href={`mailto:${FP_SUPPORT_EMAIL}`}
                      onClick={customersBulkUploadClickHandler}>
                      Contact us
                    </a>{' '}
                    and we’ll import your customers in bulk.
                  </span>
                }
              />
              <CustomerForm
                defaultValues={defaultValues}
                onSuccess={addNewCustomerHandler}
                onCancel={cancelAddEntityHandler}
                isLoading={isApiLoading}
              />
            </AddEntityModal>
          )}

          {isFormVisible && customerProp?.id && (
            <AddEntityModal
              open
              title='Edit Customer'
              error={apiError}
              dataTest='edit-customer-modal'
              onClose={cancelAddEntityHandler}>
              <CustomerContact
                customer={{
                  ...customerProp,
                  notes: customerNotesProp,
                }}
                customerContact={customerContactProp}
                onChange={customerContactChangeHandler}
                onCancel={cancelAddEntityHandler}
              />
            </AddEntityModal>
          )}
        </div>

        {/* Customer Display Tile */}
        {customerProp && (
          <DisplayTile className='mt2'>
            <DisplayTileHeader>
              <DisplayTileHeaderAction>
                <IconButton
                  Icon={EditIcon}
                  onClick={editCustomerClickHandler}
                  data-test='edit-customer-contact'
                  label='Edit Contact'
                  size='large'
                />
              </DisplayTileHeaderAction>
              <div>{customerProp?.name}</div>
            </DisplayTileHeader>
            <DisplayTileSection>
              <p className='text-small text-default b'>Customer Contact</p>
              <div className='mb1'>{customerContactProp?.name || 'No contact name'}</div>
              {customerContactProp?.email ? (
                <a href={`mailto:${customerContactProp?.email}`}>{customerContactProp?.email}</a>
              ) : (
                'No email'
              )}
              <Bullet />
              <span className='fs-mask'>
                {customerContactProp?.phone
                  ? formatPhone(customerContactProp?.phone, {
                      removeCountryCode: '+1',
                    })
                  : 'No phone number'}
              </span>
              {customerContactProp?.phoneExt && <span> ext {customerContactProp?.phoneExt}</span>}
            </DisplayTileSection>
            <DisplayTileSection>
              <p className='text-small text-default b mb1'>Customer Notes</p>
              <span>{customerNotesProp || DISPLAY.empty}</span>
            </DisplayTileSection>
          </DisplayTile>
        )}
      </>
    )
  }
)
